import React from 'react';
import SearchBar from '../../SearchBar/SearchBar';
import './LoadManagementHeader.scss';
import { SearchTypeEnum } from '../../../types/SearchTypeEnum';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { useStoreContext } from '../../App/StoreProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';

interface SelectedLoad {
    loadId: number | null;
    // boxNumber: string | null;
}

export enum SortOrder {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

interface LoadManagementHeaderProps {
    onCheckAll: (checkedStatus: boolean) => void;
    selectedLoads: SelectedLoad[];
    openSelectedLoads: () => void;
    deleteSelectedLoads: () => void;
    isSearchOnLoadId: boolean;
    setIsSearchOnLoadId: React.Dispatch<React.SetStateAction<boolean>>;
    showFilter: boolean;
    setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
    dateRangeFilter: string | null;
    setDateRangeFilter: React.Dispatch<React.SetStateAction<string | null>>;
    isDateRangeDisabled?: boolean;
}

const LoadManagementHeader = ({
    onCheckAll,
    selectedLoads,
    deleteSelectedLoads,
    isSearchOnLoadId,
    setIsSearchOnLoadId,
    showFilter,
    setShowFilter,
    dateRangeFilter,
    setDateRangeFilter,
    isDateRangeDisabled = false,
}: LoadManagementHeaderProps): React.ReactElement => {
    const isDeleteLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.DeleteLoad);

    const [state, dispatch] = useStoreContext();
    const {
        loadManagementList: { filters, searchPhrase },
    } = state;

    const getDateRangeLabel = () => {
        switch (dateRangeFilter) {
            case 'alltime':
                return 'All Time';
            case '1week':
                return '1 Week';
            case '1month':
                return '1 Month';
            case '3months':
                return '3 Months';
            default:
                return 'Select Range';
        }
    };

    return (
        <div className="Header-Container">
            <div
                className="Filter-HeaderIconContainer JobLoads-Filter"
                onClick={() => setShowFilter(!showFilter)}
                style={{ position: 'relative' }}
            >
                <i className="material-icons">filter_list</i>
                {((filters.statuses && filters.statuses.length > 0) ||
                    (filters.customerIds && filters.customerIds.length > 0) ||
                    (filters.regionIds && filters.regionIds.length > 0) ||
                    (filters.districtIds && filters.districtIds.length > 0) ||
                    (filters.fromDate && filters.fromDate.length > 0) ||
                    (filters.toDate && filters.toDate.length > 0) ||
                    (filters.jobIds && filters.jobIds.length > 0) ||
                    (filters.jobStatuses && filters.jobStatuses.length > 0) ||
                    (filters.lofaIds && filters.lofaIds.length > 0) ||
                    (filters.proppantIds && filters.proppantIds.length > 0) ||
                    (filters.driverIds && filters.driverIds.length > 0) ||
                    (filters.carrierIds && filters.carrierIds.length > 0)) && (
                    <FontAwesomeIcon
                        icon={['fas', 'check-circle']}
                        color="#fb6e3a"
                        size="sm"
                        style={{ position: 'absolute', top: '10px', right: '-4px' }}
                    />
                )}
            </div>

            <div className="LoadManagementHeader-Search-Container">
                <div className="LoadManagementHeader-Search-Filters-Container">
                    <div className="LoadManagementHeader-Search-Filter">
                        <input
                            type="radio"
                            id="loadId"
                            name="isLoadId"
                            checked={isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(true)}
                        />
                        <label htmlFor="loadId">Load Id</label>
                    </div>
                    <div className="LoadManagementHeader-Search-Filter LoadManagementHeader-Search-Filter-SearchAll">
                        <input
                            type="radio"
                            id="searchAll"
                            name="isLoadId"
                            checked={!isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(false)}
                        />
                        <label htmlFor="searchAll">Search All</label>
                    </div>
                </div>

                <div className="Header-SearchBar" style={{ display: 'flex', alignItems: 'center' }}>
                    <SearchBar type={SearchTypeEnum.LoadManagement}></SearchBar>
                    
                    <Dropdown style={{ marginLeft: '10px', marginTop: '2px' }}>
                        <Dropdown.Toggle 
                            variant="outline-primary" 
                            id="date-range-dropdown" 
                            size="sm"
                            disabled={isDateRangeDisabled}
                            style={{
                                borderColor: isDateRangeDisabled ? '#ccc' : '#0091da',
                                color: isDateRangeDisabled ? '#999' : '#0091da',
                                backgroundColor: isDateRangeDisabled ? '#f5f5f5' : 'white',
                                fontFamily: 'Avenir-Medium',
                                fontSize: '11pt',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 12px',
                                cursor: isDateRangeDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDateRangeDisabled ? 0.6 : 1
                            }}
                        >
                            <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle', marginRight: '5px' }}>
                                date_range
                            </i>
                            {getDateRangeLabel()}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item 
                                active={dateRangeFilter === 'alltime'}
                                onClick={() => setDateRangeFilter('alltime')}
                            >
                                All Time
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                                active={dateRangeFilter === '1week'}
                                onClick={() => setDateRangeFilter('1week')}
                            >
                                1 Week
                            </Dropdown.Item>
                            <Dropdown.Item 
                                active={dateRangeFilter === '1month'}
                                onClick={() => setDateRangeFilter('1month')}
                            >
                                1 Month
                            </Dropdown.Item>
                            <Dropdown.Item 
                                active={dateRangeFilter === '3months'}
                                onClick={() => setDateRangeFilter('3months')}
                            >
                                3 Months
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="Header-ListHeader">
                <div className="Header-LeftOptions">
                    <input
                        type="checkbox"
                        className="Header-Checkbox"
                        onChange={(event): void => onCheckAll(event.currentTarget.checked)}
                    />
                    <div className="Row">
                        <span className="TableListHeader-Link">{`Select All Created (${selectedLoads.length} selected)`}</span>
                    </div>
                    <span className="Header-Divider">|</span>
                    <div
                        className={`Row Header-GroupLink ${
                            isDeleteLoadEnabled ? '' : 'LoadManagementHeader-DeleteButton-Disabled'
                        }`}
                        onClick={deleteSelectedLoads}
                    >
                        <i className="material-icons Header-Icon">delete</i>
                        <span className="TableListHeader-Link">Delete Load</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadManagementHeader;

